import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DashboardLayout } from './components/Layout/DashboardLayout';
import { Login } from './components/Auth/Login';
import { Register } from './components/Auth/Register';
import { ForgotPassword } from './components/Auth/ForgotPassword';
import { VerifyEmail } from './components/Auth/VerifyEmail';
import { DashboardOverview } from './components/Dashboard/DashboardOverview';
import { ScansList } from './components/Scans/ScansList';
import { ScanDetails } from './components/Scans/ScanDetails';
import { FalsePositiveReports } from './components/Scans/FalsePositiveReports';
import { CLIToken } from './components/Tokens/CLIToken';
import { Profile } from './components/Users/Profile';
import { PricingPlans } from './components/Billing/PricingPlans';
import { Analytics } from './components/Analytics/Analytics';
import { HomePage } from './components/Home/HomePage';
import { AboutUs } from './components/Pages/AboutUs';
import { ContactUs } from './components/Pages/ContactUs';
import { PricingPage } from './components/Pages/PricingPage';
import { PrivacyPolicy } from './components/Pages/PrivacyPolicy';
import { RefundPolicy } from './components/Pages/RefundPolicy';
import { TermsAndConditions } from './components/Pages/TermsAndConditions';
import { Docs } from './components/Pages/Docs';
import { Careers } from './components/Pages/Careers';
import { Features } from './components/Pages/Features';
import { Changelog } from './components/Pages/Changelog';
import { Roadmap } from './components/Pages/Roadmap';

// Two-tone theme: primary #FF7518, background #ffffff
const theme = createTheme({
  palette: {
    primary: {
      main: '#FF7518',
      light: '#8b6914',
      dark: '#4a2d08',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#4a2d08',
      contrastText: '#ffffff',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    divider: 'rgba(0,0,0,0.08)',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontWeight: 600, color: '#4a2d08' },
    h2: { fontWeight: 600, color: '#4a2d08' },
    h3: { fontWeight: 600, color: '#4a2d08' },
    h4: { fontWeight: 600, color: '#4a2d08' },
    h5: { fontWeight: 600, color: '#4a2d08' },
    h6: { fontWeight: 600, color: '#4a2d08' },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          border: '1px solid',
          borderColor: 'divider',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 10,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'divider',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          // Bold text (fontWeight 600/700) uses heading color
          '&[style*="font-weight: 600"], &[style*="font-weight: 700"], &[style*="font-weight: bold"]': {
            color: '#4a2d08',
          },
        },
        h1: { color: '#4a2d08' },
        h2: { color: '#4a2d08' },
        h3: { color: '#4a2d08' },
        h4: { color: '#4a2d08' },
        h5: { color: '#4a2d08' },
        h6: { color: '#4a2d08' },
      },
    },
  },
});

const isLocalEnv = process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';

// Root Route: landing page for guests, dashboard for authenticated users
function RootRoute() {
  const { currentUser, userClaims } = useAuth();
  const isAdmin = userClaims?.role === 'admin';

  if (!currentUser) return <HomePage />;
  if (!currentUser.emailVerified && !isAdmin && !isLocalEnv) return <Navigate to="/verify-email" replace />;

  return <DashboardLayout><DashboardOverview /></DashboardLayout>;
}

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, userClaims } = useAuth();
  const isAdmin = userClaims?.role === 'admin';

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  if (!currentUser.emailVerified && !isAdmin && !isLocalEnv) {
    return <Navigate to="/verify-email" replace />;
  }

  return <DashboardLayout>{children}</DashboardLayout>;
}

// Public Route Component (redirects if authenticated and verified)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { currentUser, userClaims } = useAuth();
  const isAdmin = userClaims?.role === 'admin';
  const canAccess = currentUser?.emailVerified || isAdmin || isLocalEnv;

  if (currentUser && canAccess) {
    return <Navigate to="/" replace />;
  }

  if (currentUser && !canAccess) {
    return <Navigate to="/verify-email" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Landing Page */}
      <Route path="/home" element={<HomePage />} />

      {/* Public Info Pages */}
      <Route path="/about" element={<AboutUs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/plans" element={<PricingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/terms" element={<TermsAndConditions />} />
      <Route path="/docs" element={<Docs />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/features" element={<Features />} />
      <Route path="/changelog" element={<Changelog />} />
      <Route path="/roadmap" element={<Roadmap />} />

      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPassword />
          </PublicRoute>
        }
      />
      <Route path="/verify-email" element={<VerifyEmail />} />

      {/* Root: landing page for guests, dashboard for authenticated users */}
      <Route path="/" element={<RootRoute />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardOverview />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scans"
        element={
          <ProtectedRoute>
            <ScansList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/token"
        element={
          <ProtectedRoute>
            <CLIToken />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/scans/:id"
        element={
          <ProtectedRoute>
            <ScanDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/false-positive-reports"
        element={
          <ProtectedRoute>
            <FalsePositiveReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pricing"
        element={
          <ProtectedRoute>
            <PricingPlans />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <Analytics />
          </ProtectedRoute>
        }
      />
      
      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
